import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

export interface EngagementData {
  comment_id: number;
  opportunity_id: number;
  reddit_comment_id: string;
  upvotes: number;
  downvotes: number;
  score: number;
  reply_count: number;
  positive_replies: number;
  negative_replies: number;
  neutral_replies: number;
  engagement_rate: number;
  last_checked: string;
  created_at: string;
}

export interface OpportunityEngagement {
  opportunity_id: number;
  title: string;
  subreddit: string;
  comments_posted: number;
  comments_with_engagement: number;
  total_upvotes: number;
  total_downvotes: number;
  total_score: number;
  total_replies: number;
  avg_engagement_rate: number;
  sentiment_breakdown: {
    positive: number;
    negative: number;
    neutral: number;
  };
  comments: Array<{
    comment_id: number;
    reddit_comment_id: string;
    posted_at: string;
    upvotes: number;
    score: number;
    replies: number;
    engagement_rate: number;
  }>;
}

export interface ProjectEngagement {
  project_id: number;
  project_name: string;
  total_comments: number;
  comments_with_engagement: number;
  total_upvotes: number;
  total_downvotes: number;
  total_score: number;
  total_replies: number;
  avg_engagement_rate: number;
  sentiment_breakdown: {
    positive: number;
    negative: number;
    neutral: number;
  };
  best_performing_comment: {
    comment_id: number;
    opportunity_id: number;
    title: string;
    subreddit: string;
    upvotes: number;
    score: number;
    replies: number;
    engagement_rate: number;
    posted_at: string;
  } | null;
  recent_conversations: Array<{
    thread_id: number;
    opportunity_id: number;
    title: string;
    subreddit: string;
    status: string;
    responses: number;
    positive: number;
    negative: number;
    last_response: string;
  }>;
}

export interface Conversation {
  id: number;
  opportunity_id: number;
  project_id: number;
  project_name: string;
  title: string;
  subreddit: string;
  status: string;
  reddit_post_id: string;
  original_comment_id: string;
  responses_detected: number;
  positive_responses: number;
  negative_responses: number;
  questions_received: number;
  last_response_at: string;
  last_checked_at: string;
}

export interface ConversationDetails {
  thread: {
    id: number;
    opportunity_id: number;
    title: string;
    subreddit: string;
    post_url: string;
    status: string;
    reddit_post_id: string;
    original_comment_id: string;
    responses_detected: number;
    positive_responses: number;
    negative_responses: number;
    questions_received: number;
    last_response_at: string;
  };
  replies: Array<{
    id: number;
    author: string;
    text: string;
    sentiment: string;
    intent: string;
    is_question: boolean;
    buying_intent_score: number;
    created_at: string;
  }>;
}

/**
 * Hook to fetch engagement data for a specific comment
 */
export function useCommentEngagement(commentId: number | undefined) {
  return useQuery({
    queryKey: ['engagement', 'comment', commentId],
    queryFn: async () => {
      if (!commentId) return null;

      const response = await apiClient.request<EngagementData>(
        `/wp-json/redd-miner-leads/v1/engagement/comment/${commentId}`
      );
      return response;
    },
    enabled: !!commentId,
    staleTime: 30000, // Consider data stale after 30 seconds
  });
}

/**
 * Hook to fetch engagement data for an opportunity
 */
export function useOpportunityEngagement(opportunityId: number | undefined) {
  return useQuery({
    queryKey: ['engagement', 'opportunity', opportunityId],
    queryFn: async () => {
      if (!opportunityId) return null;

      const response = await apiClient.request<OpportunityEngagement>(
        `/wp-json/redd-miner-leads/v1/engagement/opportunity/${opportunityId}`
      );
      return response;
    },
    enabled: !!opportunityId,
    staleTime: 60000, // Consider data stale after 1 minute
  });
}

/**
 * Hook to fetch engagement data for a project
 */
export function useProjectEngagement(projectId: number | undefined) {
  return useQuery({
    queryKey: ['engagement', 'project', projectId],
    queryFn: async () => {
      if (!projectId) return null;

      const response = await apiClient.request<ProjectEngagement>(
        `/wp-json/redd-miner-leads/v1/engagement/project/${projectId}`
      );
      return response;
    },
    enabled: !!projectId,
    staleTime: 60000, // Consider data stale after 1 minute
  });
}

/**
 * Hook to fetch all conversations
 */
export function useConversations(filters?: { status?: string; project_id?: number }) {
  return useQuery({
    queryKey: ['engagement', 'conversations', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.project_id) params.append('project_id', filters.project_id.toString());

      const url = `/wp-json/redd-miner-leads/v1/engagement/conversations${
        params.toString() ? `?${params.toString()}` : ''
      }`;

      const response = await apiClient.request<Conversation[]>(url);
      return response;
    },
    staleTime: 30000, // Consider data stale after 30 seconds
  });
}

/**
 * Hook to fetch conversation details with all replies
 */
export function useConversationDetails(threadId: number | undefined) {
  return useQuery({
    queryKey: ['engagement', 'conversation', threadId],
    queryFn: async () => {
      if (!threadId) return null;

      const response = await apiClient.request<ConversationDetails>(
        `/wp-json/redd-miner-leads/v1/engagement/conversation/${threadId}`
      );
      return response;
    },
    enabled: !!threadId,
    staleTime: 30000, // Consider data stale after 30 seconds
  });
}
